interface CustomMappings {
  [key: string]: string;
}

class FatalConfigurationError extends Error {}
class FatalRowError extends Error {}

// The TextEquivalenceModel module provides functionality for automatically mapping source columns
// to persona fields based on text similarity. It uses a combination of
// Levenshtein distance, word similarity, and prefix matching to calculate
// the similarity between column names. The module employs a greedy matching
// algorithm with high and low confidence thresholds to determine the best mappings.
//
// Key features:
// - Preprocesses column names to normalize text
// - Calculates similarity using multiple metrics
// - Applies custom mapping rules for common field names
// - Uses a two-pass greedy matching algorithm (high and low confidence)
// - Considers mutual best matches for low confidence mappings
export class TextEquivalenceModel {
  private static readonly CUSTOM_MAPPINGS: CustomMappings = {
    id: 'reference_id',
    email: 'email_address',
    dob: 'birthdate',
    dateofbirth: 'birthdate',
    phone: 'phone_number',
    address: 'address_street',
    city: 'city',
    state: 'subdivision',
    country: 'country',
    postal: 'postal_code',
    zip: 'postal_code', // With zip instead of zip_code as the key, the model is still able to map to postal code, it also leaves room for other suffixes to follow zip while zip remains a key word for postal code.
    ssn: 'social_security_number',
    cc: 'address_country_code',
    id_class: 'identification_class',
  };

  private static readonly HIGH_CONFIDENCE_THRESHOLD = 0.8;
  private static readonly LOW_CONFIDENCE_THRESHOLD = 0.2;
  private static readonly BOOST = 0.5;
  private static readonly PARTIAL_MATCH_BOOST = 0.8;

  public static autoMapFields(
    sourceColumns: string[],
    personaFields: string[],
  ): { [key: string]: string | null } {
    if (sourceColumns.length === 0) {
      throw new FatalConfigurationError('Source columns cannot be empty');
    }
    if (personaFields.length === 0) {
      throw new FatalConfigurationError('Persona fields cannot be empty');
    }

    try {
      const preprocessedSource = this.preprocessColumns(sourceColumns);
      const preprocessedPersona = this.preprocessColumns(personaFields);

      const similarityMatrix = this.calculateSimilarityMatrix(
        preprocessedSource,
        preprocessedPersona,
        sourceColumns,
        personaFields,
      );

      this.applyCustomRules(similarityMatrix, sourceColumns, personaFields);

      return this.greedyMatching(similarityMatrix, sourceColumns, personaFields);
    } catch (e) {
      throw new FatalConfigurationError(`Failed to auto-map fields: ${(e as Error).message}`);
    }
  }

  private static preprocessColumns(columns: string[]): string[][] {
    return columns.map((col) =>
      col
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ' ')
        .split(' '),
    );
  }

  private static calculateSimilarityMatrix(
    preprocessedSource: string[][],
    preprocessedPersona: string[][],
    sourceColumns: string[],
    personaFields: string[],
  ): number[][] {
    return preprocessedSource.map((s, i) =>
      preprocessedPersona.map((p, j) =>
        this.calculateSimilarity(
          s,
          p,
          sourceColumns[i].toLowerCase(),
          personaFields[j].toLowerCase(),
        ),
      ),
    );
  }

  private static calculateSimilarity(
    sourceWords: string[],
    personaWords: string[],
    sourceOriginal: string,
    personaOriginal: string,
  ): number {
    if (sourceWords.length === 0) {
      throw new FatalConfigurationError('Source words cannot be empty');
    }
    if (personaWords.length === 0) {
      throw new FatalConfigurationError('Persona words cannot be empty');
    }

    try {
      const levenshteinSimilarity =
        1 -
        this.levenshteinDistance(sourceOriginal, personaOriginal) /
          Math.max(sourceOriginal.length, personaOriginal.length);
      const wordSimilarity = this.calculateWordSimilarity(sourceWords, personaWords);
      const prefixSimilarity = this.calculatePrefixSimilarity(sourceOriginal, personaOriginal);

      const exactMatchBonus = sourceOriginal === personaOriginal ? this.BOOST : 0;

      // Similarity score calculation with weighted components:
      // - Levenshtein similarity (40%): Accounts for overall string similarity, handling typos and small variations.
      // - Word similarity (30%): Captures semantic similarity, especially useful for compound words or different word orders.
      // - Prefix similarity (30%): Gives importance to matching beginnings of strings, which often indicate field type.
      // - Exact match bonus (0.5): Strongly favors exact matches, ensuring they're almost always chosen if present.
      //
      // These weights were determined through empirical backtesting on user data extracted from snowflake. From testing, these coefficients provide a balance
      // between different aspects of string similarity while strongly favoring exact matches.
      //
      // Note: We can, in future iteration, adjust the weights based on ongoing performance analysis and specific use case requirements.

      return (
        levenshteinSimilarity * 0.4 +
        wordSimilarity * 0.3 +
        prefixSimilarity * 0.3 +
        exactMatchBonus
      );
    } catch (e) {
      if (e instanceof Error && e.message.includes('division by zero')) {
        return 0.0;
      }

      throw new FatalRowError(`Failed to calculate similarity: ${(e as Error).message}`);
    }
  }

  private static calculateWordSimilarity(sourceWords: string[], personaWords: string[]): number {
    const sourceSet = new Set(sourceWords);
    const personaSet = new Set(personaWords);

    const overlap = new Set([...sourceSet].filter((x) => personaSet.has(x))).size;
    const total = new Set([...sourceSet, ...personaSet]).size;

    // Check for partial word matches, excluding exact matches
    const partialMatches = sourceWords.reduce(
      (sum, sw) =>
        sum + personaWords.filter((pw) => sw !== pw && (sw.includes(pw) || pw.includes(sw))).length,
      0,
    );

    return (overlap + partialMatches * this.PARTIAL_MATCH_BOOST) / total;
  }

  private static calculatePrefixSimilarity(sourceKey: string, personaKey: string): number {
    const minLength = Math.min(sourceKey.length, personaKey.length);
    let commonPrefixLength = 0;
    for (let i = 0; i < minLength; i++) {
      if (sourceKey[i] !== personaKey[i]) break;
      commonPrefixLength++;
    }
    return commonPrefixLength / minLength;
  }

  private static applyCustomRules(
    matrix: number[][],
    sourceColumns: string[],
    personaFields: string[],
  ): void {
    if (matrix.length === 0) {
      throw new FatalConfigurationError('Matrix cannot be empty');
    }
    if (sourceColumns.length === 0) {
      throw new FatalConfigurationError('Source columns cannot be empty');
    }
    if (personaFields.length === 0) {
      throw new FatalConfigurationError('Persona fields cannot be empty');
    }

    try {
      Object.entries(this.CUSTOM_MAPPINGS).forEach(([sourceKey, personaKey]) => {
        const sourceIndex = sourceColumns.findIndex((c) => c.toLowerCase().includes(sourceKey));
        const personaIndex = personaFields.findIndex((f) => f.toLowerCase().includes(personaKey));
        if (sourceIndex !== -1 && personaIndex !== -1) {
          matrix[sourceIndex][personaIndex] += this.BOOST;
        }
      });
    } catch (e) {
      throw new FatalConfigurationError(`Failed to apply custom rules: ${(e as Error).message}`);
    }
  }

  private static levenshteinDistance(sourceKey: string, personaKey: string): number {
    const m = sourceKey.length; // If persona_key is empty, distance is length of source_key
    const n = personaKey.length; // If source_key is empty, distance is length of persona_key
    if (n === 0) return m;
    if (m === 0) return n;

    // Initialize the distance matrix
    const d: number[][] = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0));

    // Initialize first row and column of matrix
    for (let i = 0; i <= m; i++) d[i][0] = i;
    for (let j = 0; j <= n; j++) d[0][j] = j;

    // Compute the distance matrix
    for (let j = 1; j <= n; j++) {
      for (let i = 1; i <= m; i++) {
        if (sourceKey[i - 1] === personaKey[j - 1]) {
          // If characters match, cost is 0
          d[i][j] = d[i - 1][j - 1];
        } else {
          // If characters don't match, take the minimum of three operations:
          // insertion, deletion, or substitution
          d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + 1);
        }
      }
    }

    // Return the Levenshtein distance (bottom-right cell of the matrix)
    return d[m][n];
  }

  private static greedyMatching(
    similarityMatrix: number[][],
    sourceColumns: string[],
    personaFields: string[],
  ): { [key: string]: string | null } {
    if (similarityMatrix.length === 0) {
      throw new FatalConfigurationError('Similarity matrix cannot be empty');
    }
    if (similarityMatrix.length !== sourceColumns.length) {
      throw new FatalConfigurationError('Source columns and similarity matrix size mismatch');
    }

    try {
      const mapping: { [key: string]: string | null } = {};
      const unmatchedSources = new Set(Array.from({ length: sourceColumns.length }, (_, i) => i));
      const unmatchedPersonas = new Set(Array.from({ length: personaFields.length }, (_, i) => i));

      unmatchedSources.forEach((sourceIndex) => {
        // Find top 3 high-confidence matches for the current source column
        const bestMatches = similarityMatrix[sourceIndex]
          .map((sim, index) => ({ sim, index }))
          .filter(({ sim }) => sim >= this.HIGH_CONFIDENCE_THRESHOLD)
          .sort((a, b) => b.sim - a.sim)
          .slice(0, 3);

        // Find the first high-confidence match that is still unmatched
        const bestMatch = bestMatches.find(({ index }) => unmatchedPersonas.has(index));

        // If a high-confidence match is found, add it to the mapping
        if (bestMatch) {
          mapping[sourceColumns[sourceIndex]] = personaFields[bestMatch.index];
          unmatchedSources.delete(sourceIndex);
          unmatchedPersonas.delete(bestMatch.index);
        } else {
          // If no high-confidence match, try low-confidence matching
          // Find top 3 candidates from remaining unmatched persona fields
          const candidates = Array.from(unmatchedPersonas)
            .map((pIndex) => ({ sim: similarityMatrix[sourceIndex][pIndex], index: pIndex }))
            .sort((a, b) => b.sim - a.sim)
            .slice(0, 3);

          // Find the best low-confidence match that meets all criteria
          const bestLowConfidenceMatch = candidates.find(
            ({ sim, index }) =>
              sim >= this.LOW_CONFIDENCE_THRESHOLD &&
              unmatchedPersonas.has(index) &&
              this.isMutualBestMatch(
                similarityMatrix,
                sourceIndex,
                index,
                unmatchedSources,
                unmatchedPersonas,
              ),
          );

          // If a low-confidence match is found, add it to the mapping
          if (bestLowConfidenceMatch) {
            mapping[sourceColumns[sourceIndex]] = personaFields[bestLowConfidenceMatch.index];
            unmatchedPersonas.delete(bestLowConfidenceMatch.index);
          } else {
            // If no match found, map the source column to null
            mapping[sourceColumns[sourceIndex]] = null;
          }
        }
      });

      return mapping;
    } catch (e) {
      throw new FatalConfigurationError(`Failed in greedy matching: ${(e as Error).message}`);
    }
  }

  private static isMutualBestMatch(
    similarityMatrix: number[][],
    sourceIndex: number,
    personaIndex: number,
    unmatchedSources: Set<number>,
    unmatchedPersonas: Set<number>,
  ): boolean {
    const sourceBest = similarityMatrix[sourceIndex]
      .map((sim, i) => ({ sim: unmatchedPersonas.has(i) ? sim : -Infinity, index: i }))
      .reduce((max, current) => (current.sim > max.sim ? current : max));

    const personaBest = Array.from(unmatchedSources).reduce((max, i) =>
      similarityMatrix[i][personaIndex] > similarityMatrix[max][personaIndex] ? i : max,
    );

    return sourceBest.index === personaIndex && personaBest === sourceIndex;
  }
}
